import { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { assessmentApi } from '../api/client';
import { Assessment, Question, SubmissionResponse } from '../types';
import { CheckCircle, XCircle, Clock, Trophy } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../api/client';

type QuizState = 'loading' | 'taking' | 'submitting' | 'result';

export function QuizPage() {
  const { courseId, assessmentId } = useParams();
  const navigate                   = useNavigate();
  const [state, setState]          = useState<QuizState>('loading');
  const [assessment, setAssessment] = useState<Assessment | null>(null);
  const [questions, setQuestions]  = useState<Question[]>([]);
  const [answers, setAnswers]      = useState<Record<string, string>>({});
  const [result, setResult]        = useState<SubmissionResponse | null>(null);
  const [timeLeft, setTimeLeft]    = useState<number | null>(null);
  const [startTime]                = useState(Date.now());

  // ── handleSubmit defined FIRST before useEffects ─────────
  const handleSubmit = useCallback(async () => {
    if (!assessmentId || state === 'submitting') return;
    setState('submitting');

    const timeTaken = Math.floor((Date.now() - startTime) / 1000);
    const answerList = questions.map(q => ({
      questionId:    q.id,
      studentAnswer: answers[q.id] || '',
    }));

    try {
      const res = await assessmentApi.submit(
        assessmentId, answerList, timeTaken
      );
      setResult(res.data);
      setState('result');
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Submission failed');
      setState('taking');
    }
  }, [assessmentId, answers, questions, state, startTime]);

  // ── Load quiz ─────────────────────────────────────────────
  useEffect(() => {
    if (!assessmentId) return;
    async function load() {
      try {
        const res = await api.get(
          `/api/v1/assessments/${assessmentId}/questions`
        );
        const assessmentRes = await api.get(
          `/api/v1/assessments/${assessmentId}`
        );
        setQuestions(res.data);
        setAssessment(assessmentRes.data);
        if (assessmentRes.data.timeLimitMins) {
          setTimeLeft(assessmentRes.data.timeLimitMins * 60);
        }
        setState('taking');
      } catch {
        toast.error('Failed to load quiz');
        navigate(`/courses/${courseId}`);
      }
    }
    load();
  }, [assessmentId, courseId, navigate]);

  // ── Countdown timer ───────────────────────────────────────
  useEffect(() => {
    if (timeLeft === null || state !== 'taking') return;
    if (timeLeft <= 0) {
      handleSubmit();
      return;
    }
    const timer = setTimeout(
      () => setTimeLeft(t => (t ?? 1) - 1), 1000
    );
    return () => clearTimeout(timer);
  }, [timeLeft, state, handleSubmit]);

  const formatTime = (secs: number) => {
    const m = Math.floor(secs / 60);
    const s = secs % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  // ── Loading ───────────────────────────────────────────────
  if (state === 'loading') {
    return (
      <div className="max-w-3xl mx-auto px-6 py-8 text-gray-500">
        Loading quiz...
      </div>
    );
  }

  // ── Result screen ─────────────────────────────────────────
  if (state === 'result' && result) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-8">
        <div className={`rounded-2xl p-6 mb-6 text-center border
          ${result.passed
            ? 'bg-green-400/10 border-green-400/30'
            : 'bg-red-400/10 border-red-400/30'
          }`}>
          <Trophy
            className={`mx-auto mb-3
              ${result.passed ? 'text-green-400' : 'text-red-400'}`}
            size={40}
          />
          <div className={`text-4xl font-black mb-1
            ${result.passed ? 'text-green-400' : 'text-red-400'}`}>
            {result.score}%
          </div>
          <div className="text-white font-bold text-lg mb-1">
            {result.passed ? '🎉 Passed!' : 'Keep trying!'}
          </div>
          <div className="text-gray-400 text-sm">
            {result.earnedMarks} / {result.totalMarks} marks ·
            +{result.xpEarned} XP earned
          </div>
        </div>

        <h2 className="text-lg font-bold text-white mb-4">
          Question Review
        </h2>
        <div className="space-y-4 mb-6">
          {result.results.map((r, i) => (
            <div key={r.questionId}
                 className={`border rounded-xl p-4
                   ${r.isCorrect
                     ? 'border-green-400/30 bg-green-400/5'
                     : 'border-red-400/30 bg-red-400/5'
                   }`}>
              <div className="flex items-start gap-3">
                {r.isCorrect
                  ? <CheckCircle size={16}
                      className="text-green-400 mt-0.5 flex-shrink-0" />
                  : <XCircle size={16}
                      className="text-red-400 mt-0.5 flex-shrink-0" />
                }
                <div className="flex-1">
                  <div className="text-white text-sm font-semibold mb-2">
                    Q{i + 1}. {r.questionText}
                  </div>
                  <div className="text-xs space-y-1">
                    <div className="text-gray-400">
                      Your answer:{' '}
                      <span className={r.isCorrect
                        ? 'text-green-400' : 'text-red-400'}>
                        {r.studentAnswer || 'Not answered'}
                      </span>
                    </div>
                    {!r.isCorrect && (
                      <div className="text-gray-400">
                        Correct:{' '}
                        <span className="text-green-400">
                          {r.correctAnswer}
                        </span>
                      </div>
                    )}
                    {r.explanation && (
                      <div className="text-gray-500 mt-2 italic">
                        {r.explanation}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        <button
          onClick={() => navigate(`/courses/${courseId}`)}
          className="w-full bg-green-400 text-gray-950 font-bold
                     py-3 rounded-xl hover:bg-green-300 transition-colors"
        >
          Back to Course
        </button>
      </div>
    );
  }

  // ── Quiz taking screen ────────────────────────────────────
  const answeredCount = Object.keys(answers).length;

  return (
    <div className="max-w-3xl mx-auto px-6 py-8">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-black text-white">
            {assessment?.title}
          </h1>
          <div className="text-xs text-gray-500 mt-1">
            {answeredCount} of {questions.length} answered
          </div>
        </div>
        {timeLeft !== null && (
          <div className={`flex items-center gap-2 font-bold
                           text-sm px-3 py-2 rounded-lg border
            ${timeLeft < 60
              ? 'text-red-400 border-red-400/30 bg-red-400/10 animate-pulse'
              : 'text-gray-300 border-gray-700'
            }`}>
            <Clock size={14} />
            {formatTime(timeLeft)}
          </div>
        )}
      </div>

      <div className="h-1 bg-gray-800 rounded-full mb-6">
        <div
          className="h-1 bg-green-400 rounded-full transition-all"
          style={{
            width: `${(answeredCount / questions.length) * 100}%`
          }}
        />
      </div>

      <div className="space-y-6 mb-8">
        {questions.map((q, qi) => (
          <div key={q.id}
               className="bg-gray-900 border border-gray-800
                          rounded-xl p-5">
            <div className="flex items-start gap-3 mb-4">
              <span className="w-6 h-6 rounded-full bg-gray-800
                               flex items-center justify-center
                               text-xs font-bold text-gray-400
                               flex-shrink-0 mt-0.5">
                {qi + 1}
              </span>
              <p className="text-white text-sm font-medium
                            leading-relaxed">
                {q.questionText}
              </p>
            </div>

            {q.questionType === 'MCQ' ||
             q.questionType === 'TRUE_FALSE' ? (
              <div className="space-y-2 ml-9">
                {q.options.map(opt => (
                  <button
                    key={opt.id}
                    onClick={() => setAnswers(prev => ({
                      ...prev, [q.id]: opt.id
                    }))}
                    className={`w-full text-left px-4 py-3
                               rounded-xl text-sm transition-all border
                      ${answers[q.id] === opt.id
                        ? 'bg-green-400/20 border-green-400/60 text-green-300 font-semibold'
                        : 'bg-gray-800 border-gray-700 text-gray-300 hover:border-gray-600'
                      }`}
                  >
                    <span className="font-bold mr-3 text-gray-500">
                      {opt.id}.
                    </span>
                    {opt.text}
                  </button>
                ))}
              </div>
            ) : (
              <input
                value={answers[q.id] || ''}
                onChange={e => setAnswers(prev => ({
                  ...prev, [q.id]: e.target.value
                }))}
                placeholder="Type your answer..."
                className="ml-9 w-full bg-gray-800 border border-gray-700
                           rounded-xl px-4 py-3 text-white text-sm
                           focus:outline-none focus:border-green-400"
              />
            )}
          </div>
        ))}
      </div>

      <button
        onClick={handleSubmit}
        disabled={state === 'submitting'}
        className="w-full bg-green-400 text-gray-950 font-black
                   py-4 rounded-xl hover:bg-green-300 transition-colors
                   disabled:opacity-50 text-sm"
      >
        {state === 'submitting'
          ? 'Submitting...'
          : `Submit Quiz (${answeredCount}/${questions.length} answered)`
        }
      </button>
    </div>
  );
}